import React, { useState, useEffect, useCallback } from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import axios from 'axios';
import MainLayout from '../../../layout/main-layout';
import { APP_API_URL, APP_NAME } from '../../../utils';
import formatNumber, { defaultFilterDates, formatSummaryQuantityLabel } from '../../../utils/functions';

const PER_PAGE = 10;

const emptyStats = {
  lines_count: 0,
  lines_with_stock_count: 0,
  total_stock_value: 0,
  total_quantity_in_stock_xof: 0,
  total_quantity_validated_xof: 0,
};

const stockQty = (item) => parseFloat(item.quantity_in_stock || 0);

const lineStockValue = (item) =>
  parseFloat(item.stock_value ?? 0) ||
  stockQty(item) * parseFloat(item.prix_unitaire_revient ?? item.variant_price ?? 0);

const qtyLabel = (quantity, row) => formatSummaryQuantityLabel(quantity, row);

const costPrice = (item) =>
  parseFloat(item.prix_unitaire_revient ?? item.variant_price ?? 0);

export default function AdminStock() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(emptyStats);
  const [isLoading, setIsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [appliedFilters, setAppliedFilters] = useState(() => {
    const { dateFrom, dateTo } = defaultFilterDates();
    return { search: '', fromDate: dateFrom, toDate: dateTo };
  });
  const [draftFilters, setDraftFilters] = useState(() => {
    const { dateFrom, dateTo } = defaultFilterDates();
    return { fromDate: dateFrom, toDate: dateTo };
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [fromRow, setFromRow] = useState(0);
  const [toRow, setToRow] = useState(0);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/products-summary`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: currentPage,
          per_page: PER_PAGE,
          search: appliedFilters.search || undefined,
          fromDate: appliedFilters.fromDate || undefined,
          toDate: appliedFilters.toDate || undefined,
        },
      });

      const paginator = response.data?.data;
      setItems(Array.isArray(paginator?.data) ? paginator.data : []);
      setStats(response.data?.stats ?? emptyStats);
      setLastPage(paginator?.last_page ?? 1);
      setTotalRows(paginator?.total ?? 0);
      setFromRow(paginator?.from ?? 0);
      setToRow(paginator?.to ?? 0);
    } catch (error) {
      console.error('Erreur lors de la récupération du stock :', error);
      setItems([]);
      setStats(emptyStats);
      setLastPage(1);
      setTotalRows(0);
      setFromRow(0);
      setToRow(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, appliedFilters]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedFilters((prev) => ({ ...prev, search: searchInput }));
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setAppliedFilters((prev) => ({
      ...prev,
      fromDate: draftFilters.fromDate,
      toDate: draftFilters.toDate,
    }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchInput('');
    const { dateFrom, dateTo } = defaultFilterDates();
    setDraftFilters({ fromDate: dateFrom, toDate: dateTo });
    setAppliedFilters({ search: '', fromDate: dateFrom, toDate: dateTo });
    setCurrentPage(1);
  };

  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < lastPage) setCurrentPage((p) => p + 1);
    else if (direction === 'prev' && currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    fetchData();
  };

  const fetchAllForExport = async () => {
    const token = localStorage.getItem('token');
    const count = stats.lines_count || totalRows || 100;
    const response = await axios.get(`${APP_API_URL}/products-summary`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        page: 1,
        per_page: count,
        for_export: true,
        search: appliedFilters.search || undefined,
        fromDate: appliedFilters.fromDate || undefined,
        toDate: appliedFilters.toDate || undefined,
      },
    });
    const paginator = response.data?.data;
    return Array.isArray(paginator?.data) ? paginator.data : items;
  };

  const buildExportRows = (rows) =>
    rows.map((item) => {
      const quantity = stockQty(item);
      const price = costPrice(item);
      const value = lineStockValue(item);
      return { item, quantity, price, value };
    });

  const exportToPDF = async () => {
    const rows = buildExportRows(await fetchAllForExport());
    const doc = new jsPDF();
    doc.text('Stock disponible', 14, 16);
    if (appliedFilters.fromDate || appliedFilters.toDate) {
      doc.setFontSize(9);
      doc.text(
        `Période : ${appliedFilters.fromDate || '…'} → ${appliedFilters.toDate || '…'}`,
        14,
        22
      );
    }
    doc.autoTable({
      head: [['Produits', 'Code', 'Format', 'Quantité', 'Coût revient']],
      body: rows.map(({ item, quantity, value }) => [
        item.product_name,
        item.sku,
        item.format || '—',
        qtyLabel(quantity, item),
        `${formatNumber(value)} XOF`,
      ]),
      startY: appliedFilters.fromDate || appliedFilters.toDate ? 28 : 24,
      theme: 'striped',
      headStyles: { fillColor: [88, 204, 2] },
      styles: { fontSize: 9 },
    });
    doc.save(`Stock ${APP_NAME}.pdf`);
  };

  const exportToExcel = async () => {
    const rows = buildExportRows(await fetchAllForExport());
    const worksheetData = rows.map(({ item, quantity, value }) => ({
      Produits: item.product_name,
      Code: item.sku,
      Format: item.format || '—',
      'Stock actuel': qtyLabel(quantity, item),
      'Valeur magasin (PRU)': `${formatNumber(value)} XOF`,
    }));
    const ws = XLSX.utils.json_to_sheet(worksheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stock');
    XLSX.writeFile(wb, `Stock ${APP_NAME}.xlsx`);
  };

  const handlePrint = async () => {
    const rows = buildExportRows(await fetchAllForExport());
    const printWindow = window.open('', '', 'height=800,width=1200');
    const tableRows = rows
      .map(
        ({ item, quantity, value }) => `
      <tr>
        <td>${item.product_name}</td>
        <td>${item.sku}</td>
        <td>${item.format || '—'}</td>
        <td>${qtyLabel(quantity, item)}</td>
        <td>${formatNumber(value)} XOF</td>
      </tr>`
      )
      .join('');

    printWindow.document.write(`
      <html><head><title>Stock disponible</title>
      <style>body{font-family:Arial,sans-serif}table{width:100%;border-collapse:collapse}
      table,th,td{border:1px solid #ccc}th,td{padding:8px;text-align:left}</style></head>
      <body><h1>Stock disponible</h1>
      ${appliedFilters.fromDate || appliedFilters.toDate ? `<p>Période : ${appliedFilters.fromDate || '…'} → ${appliedFilters.toDate || '…'}</p>` : ''}
      <table>
      <thead><tr><th>Produits</th><th>Code</th><th>Format</th><th>Quantité</th><th>Coût revient</th></tr></thead>
      <tbody>${tableRows}</tbody></table></body></html>`);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <MainLayout loading={isLoading}>
      <div className="duo-page">
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">Administration</p>
          <h1 className="duo-hero-title">Stock disponible</h1>
          <p className="duo-hero-sub mt-2 mb-0">
            Valeur magasin = stock actuel × prix de revient (PRU). La plage de dates ne filtre que les
            quantités validées sur la période ; le stock et sa valeur restent ceux du magasin aujourd&apos;hui.
          </p>
        </div>

        <div className="duo-kpi-row">
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Variantes (filtre)</p>
            <p className="duo-kpi-chip__value">{stats.lines_count}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Valeur magasin (PRU)</p>
            <p className="duo-kpi-chip__value" style={{ fontSize: '1rem' }}>
              {formatNumber(stats.total_stock_value ?? stats.total_quantity_in_stock_xof)} XOF
            </p>
            <p className="duo-kpi-chip__hint mb-0">
              {stats.lines_with_stock_count ?? 0} variante{(stats.lines_with_stock_count ?? 0) > 1 ? 's' : ''} avec stock
            </p>
          </div>
        </div>

        <section className="duo-panel">
          <div className="duo-toolbar">
            <span className="fw-bold" style={{ color: '#3c3c3c', fontSize: '0.9rem' }}>
              Détail par produit
            </span>
            <div className="duo-toolbar__actions">
              <button type="button" title="PDF" onClick={exportToPDF} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-pdf" />
              </button>
              <button type="button" title="Excel" onClick={exportToExcel} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-xls" />
              </button>
              <button type="button" title="Imprimer" onClick={handlePrint} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-printer" />
              </button>
              <button type="button" title="Rafraîchir" onClick={handleRefresh} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-arrow-clockwise" />
              </button>
              <button
                type="button"
                title="Filtres"
                onClick={() => setFiltersOpen(!filtersOpen)}
                className={`duo-btn duo-btn--ghost duo-btn--icon duo-btn--filter${filtersOpen ? ' is-open' : ''}`}
              >
                <i className={`bi ${filtersOpen ? 'bi-x-lg' : 'bi-funnel-fill'}`} />
              </button>
            </div>
          </div>

          <div className="px-3 pt-2 pb-2 border-bottom" style={{ borderColor: '#f0f0f0' }}>
            <input
              type="search"
              className="duo-input"
              placeholder="Rechercher par référence ou SKU..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          {filtersOpen && (
            <div className="duo-filters">
              <form onSubmit={handleApplyFilters}>
                <p className="mb-2 small text-muted">
                  Plage de dates sur les <strong>bons de commande validés</strong> (quantité validée).
                  Le stock actuel n&apos;est pas filtré par cette plage.
                </p>
                <div className="duo-filters__grid">
                  <div>
                    <label className="duo-form-label">Date de début</label>
                    <input
                      type="date"
                      className="duo-input"
                      value={draftFilters.fromDate}
                      onChange={(e) => setDraftFilters((prev) => ({ ...prev, fromDate: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="duo-form-label">Date de fin</label>
                    <input
                      type="date"
                      className="duo-input"
                      value={draftFilters.toDate}
                      onChange={(e) => setDraftFilters((prev) => ({ ...prev, toDate: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="duo-btn duo-btn--ghost" onClick={handleResetFilters}>
                    Réinitialiser
                  </button>
                  <button type="submit" className="duo-btn duo-btn--primary">
                    <i className="bi bi-search" /> Appliquer les filtres
                  </button>
                </div>
              </form>
            </div>
          )}

          {(appliedFilters.fromDate || appliedFilters.toDate) && (
            <div className="px-3 py-2 border-bottom small text-muted" style={{ borderColor: '#f0f0f0' }}>
              Période active : {appliedFilters.fromDate || '…'} → {appliedFilters.toDate || '…'}
            </div>
          )}

          <div className="duo-table-wrap">
            <table className="duo-table">
              <thead>
                <tr>
                  <th>Produits</th>
                  <th>Code</th>
                  <th>Format</th>
                  <th>Stock actuel</th>
                  <th>Valeur magasin (PRU)</th>
                </tr>
              </thead>
              <tbody>
                {items.length > 0 ? (
                  items.map((item) => {
                    const quantity = stockQty(item);
                    const price = costPrice(item);
                    const value = lineStockValue(item);

                    return (
                      <tr key={item.variant_id ?? item.sku}>
                        <td className="fw-bold">{item.product_name}</td>
                        <td>{item.sku}</td>
                        <td>{item.format || '—'}</td>
                        <td className="fw-semibold" style={{ fontSize: '0.85rem' }}>
                          {qtyLabel(quantity, item)}
                        </td>
                        <td>
                          <span className="fw-semibold">{formatNumber(value)} XOF</span>
                          {price > 0 && (
                            <span className="d-block text-muted" style={{ fontSize: '0.78rem' }}>
                              {formatNumber(price)} XOF / unité revient
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} className="duo-empty-row">
                      Aucun produit correspondant aux critères.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalRows > 0 && (
            <div className="duo-footer-bar">
              <span>
                {fromRow} – {toRow} sur {totalRows} entrée{totalRows > 1 ? 's' : ''}
              </span>
              <ul className="duo-pagination">
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--ghost"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange('prev')}
                  >
                    Précédent
                  </button>
                </li>
                <li>
                  <span className="duo-pagination__info px-2">
                    Page {currentPage} / {lastPage}
                  </span>
                </li>
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--primary"
                    disabled={currentPage >= lastPage}
                    onClick={() => handlePageChange('next')}
                  >
                    Suivant
                  </button>
                </li>
              </ul>
            </div>
          )}
        </section>
      </div>
    </MainLayout>
  );
}
